"use client";
import React, {
forwardRef,
useState,
useEffect,
useRef,
useImperativeHandle,
} from "react";
import { Check, MessagesSquare, MoreVertical } from "lucide-react";
import { Conversation } from "@/app/models/interfaceConversation";
import Tooltip from "../Elements/Tooltip";
import ButtonChat from "../Elements/Core/ButtonChat";
import { Action } from "@/app/constants/enums";
import { useUser } from "@/app/context/UserContext";

interface ConversationListProps {
conversations: Conversation[];
onUpdate: (conv: Conversation) => void;
onClose: (conv: Conversation) => void;
}

/**

* Ref expuesto: ahora incluye closeAllMenus y setMenuPosition
* (setMenuPosition es opcional, pero lo expongo por si el padre quiere forzar la posición).
  */
  export interface ConversationListRef {
  closeAllMenus: () => void;
  setMenuPosition?: (pos: { top: number; left: number } | null) => void;
  }

const ConversationList = forwardRef<ConversationListRef, ConversationListProps>(
({ conversations, onUpdate, onClose }, ref) => {
const {
currentConversation,
setCurrentConversation,
setAddConversation,
setChangeConversation,
} = useUser();

 
// ---------- ESTADO NUEVO: posición del menú (para usar `position: fixed`) ----------
const [activeMenu, setActiveMenu] = useState<string | null>(null);
const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
const menuRefs = useRef<Map<string, HTMLDivElement>>(new Map());

const setConversation = (vConversation: Conversation) => {
  setAddConversation(false);
  setCurrentConversation(vConversation);
  setChangeConversation(true);
};

// ---------- toggleMenu actualizado: calcula la posición del botón y abre el menú fixed ----------
const MENU_WIDTH = 160; // coincide aproximadamente con `w-40` (10rem = 160px)
const MENU_HEIGHT = 160; // valor de respaldo para evitar overflow en altura

const toggleMenu = (id: string, event?: React.MouseEvent<HTMLElement>) => {
  // Si ya está abierto: cerramos y limpiamos posición
  if (activeMenu === id) {
    setActiveMenu(null);
    setMenuPosition(null);
    return;
  }

  // Si recibimos el evento del botón, calculamos posición respecto al viewport (fixed)
  if (event) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();

    // calcular top (intentar debajo del botón; si no cabe, colocarlo arriba)
    let top = rect.bottom + 6;
    if (top + MENU_HEIGHT > window.innerHeight) {
      top = rect.top - MENU_HEIGHT - 6;
      if (top < 8) top = 8;
    }

    // calcular left (alinear al borde derecho del botón menos el ancho del menú)
    let left = rect.right - MENU_WIDTH;
    if (left + MENU_WIDTH > window.innerWidth) left = Math.max(8, window.innerWidth - MENU_WIDTH - 8);
    if (left < 8) left = Math.max(8, rect.left);

    setMenuPosition({ top, left });
  }

  setActiveMenu(id);
};

// Click fuera: cerrar menú y limpiar posición
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (!activeMenu) return;
    const menu = menuRefs.current.get(activeMenu);
    if (menu && !menu.contains(event.target as Node)) {
      setActiveMenu(null);
      setMenuPosition(null);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, [activeMenu]);

// Si se cambia el tamaño de ventana, cerramos el menú (evita desalineación)
useEffect(() => {
  const onResize = () => {
    setActiveMenu(null);
    setMenuPosition(null);
  };
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

// Exponer métodos al padre (ahora también setMenuPosition)
useImperativeHandle(ref, () => ({
  closeAllMenus: () => {
    setActiveMenu(null);
    setMenuPosition(null);
  },
  setMenuPosition: (pos) => {
    setMenuPosition(pos ?? null);
  },
}));

return (
  <ul className="">
    {conversations.map((conversation) => (
      <li
        key={conversation.conversation_id}
        className="flex justify-between items-center cursor-pointer py-0.5"
      >
        <div
          className="flex items-center max-w-[80%] space-x-2 p-0"
          onClick={() => setConversation(conversation)}
        >
          {currentConversation?.conversation_id == conversation.conversation_id ? (
            <Check strokeWidth={1.5} className={` text-blue-500 w-6 h-6 flex-shrink-0`} />
          ) : (
            <MessagesSquare strokeWidth={1.5} className={` text-neutral-500 w-6 h-6 flex-shrink-0`} />
          )}

          <span
            className={`truncate w-full leading-tight hover:text-blue-700 ${
              currentConversation?.conversation_id == conversation.conversation_id ? "text-blue-700" : ""
            }`}
          >
            <Tooltip text={conversation.name}>{conversation.name}</Tooltip>
          </span>
        </div>

        <div className="flex items-rigth  max-w-[20%]  p-0">
          {/* onMouseDown para evitar que el listener global cierre el menú antes de que el click sea procesado */}
          <div className=" p-0" onMouseDown={(e) => e.stopPropagation()}>
            <button
              className="rounded-full text-blue-700 hover:text-blue-200 p-0"
              onClick={(e) => {
                e.stopPropagation();
                // <-- PASAMOS el evento para calcular la posición del botón
                toggleMenu(conversation.conversation_id, e);
              }}
              aria-expanded={activeMenu === conversation.conversation_id}
              aria-haspopup="true"
            >
              <MoreVertical strokeWidth={1.5} className="w-6 h-6" />
            </button>

            {/* Menú renderizado como `fixed` usando menuPosition */}
            {activeMenu === conversation.conversation_id && menuPosition && (
              <div
                key={conversation.conversation_id + "menu"}
                ref={(el) => {
                  if (el) {
                    menuRefs.current.set(conversation.conversation_id, el);
                  } else {
                    menuRefs.current.delete(conversation.conversation_id);
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: "fixed", // <-- cambio principal: fixed para evitar problemas con contenedores con overflow
                  top: menuPosition.top,
                  left: menuPosition.left,
                  zIndex: 9999,
                }}
                className="w-40 bg-white border border-gray-300 rounded-lg shadow-lg transition-all duration-300 ease-in-out animate-fadeIn"
              >
                <ButtonChat
                  actionCode={Action.LEFT_CONVERSATION_EDIT}
                  className="block w-full text-left rounded-md px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdate(conversation);
                    setActiveMenu(null);
                    setMenuPosition(null);
                  }}
                >
                  Editar
                </ButtonChat>
                <ButtonChat
                  actionCode={Action.LEFT_CONVERSATION_CLOSE}
                  type="button"
                  className="block w-full rounded-md text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(conversation);
                    setActiveMenu(null);
                    setMenuPosition(null);
                  }}
                >
                  Cerrar
                </ButtonChat>
              </div>
            )}
          </div>
        </div>
      </li>
    ))}
  </ul>
);
 

}
);

export default ConversationList;
